/**
 * Deterministic HOS-aware transit estimator.
 *
 * Code owns the arrival math so the answer is reproducible and auditable.
 * The AI never computes the delivery time — it only annotates the result.
 *
 * SOLO: FMCSA property-carrying limits — 11 h driving inside a 14 h duty
 * window, 30 min break after 8 h of driving, 10 h off-duty reset.
 * At a 55 MPH road average that's ~605 loaded miles per driving day, which
 * is why a 1,800 mi solo run lands on day 3 rather than "1,800 / 47".
 *
 * TEAM: two drivers alternating means near-continuous movement. Modeled as
 * one continuous 45.5 MPH door-to-door average (the house standard used in
 * search results), which already absorbs fuel, swaps and scale stops.
 */

export type DriverType = "solo" | "team";

/** Road average while actually driving (solo). */
export const SOLO_ROAD_MPH = 55;
/** Max driving hours per duty day (FMCSA). */
export const SOLO_DRIVE_HOURS_PER_DAY = 11;
/** Required 30-minute break after 8 cumulative driving hours. */
export const SOLO_BREAK_AFTER_HOURS = 8;
export const SOLO_BREAK_HOURS = 0.5;
/** Required off-duty reset between duty days. */
export const SOLO_RESET_HOURS = 10;
/** Team door-to-door continuous average. */
export const TEAM_DOOR_TO_DOOR_MPH = 45.5;
/** Loading allowance at the shipper before the wheels turn. */
export const LOADING_HOURS = 1;
/** Receiver hours used for the business-hours roll. */
export const RECEIVING_OPEN_HOUR = 7;
export const RECEIVING_CLOSE_HOUR = 17;

export interface TransitEstimateInput {
  miles: number;
  /** Local pickup appointment as a Date (local time semantics). */
  pickupAt: Date;
  driverType: DriverType;
  /** Extra minutes from weather / traffic / construction. Optional. */
  delayMinutes?: number;
  /** Roll arrivals outside receiving hours to the next morning. Default true. */
  respectReceivingHours?: boolean;
}

export interface TransitEstimateResult {
  /** Predicted delivery moment. */
  deliveryAt: Date;
  /** Wall-clock hours from pickup appointment to delivery. */
  totalHours: number;
  /** Pure driving hours (excludes rest, break, loading, delay). */
  drivingHours: number;
  /** Number of duty days the driving is split across (solo); 1 for team runs under a day. */
  drivingDays: number;
  /** Nights of 10 h off-duty rest taken en route (solo only). */
  restBreaks: number;
  /** Delay minutes folded into the drive. */
  delayMinutes: number;
  /** True when arrival was pushed to the next receiving window. */
  rolledToReceivingHours: boolean;
}

const HOUR_MS = 3_600_000;

function addHours(d: Date, h: number): Date {
  return new Date(d.getTime() + h * HOUR_MS);
}

function rollToReceivingWindow(d: Date): { at: Date; rolled: boolean } {
  const hour = d.getHours() + d.getMinutes() / 60;
  if (hour < RECEIVING_OPEN_HOUR) {
    const at = new Date(d);
    at.setHours(RECEIVING_OPEN_HOUR, 0, 0, 0);
    return { at, rolled: true };
  }
  if (hour >= RECEIVING_CLOSE_HOUR) {
    const at = new Date(d);
    at.setDate(at.getDate() + 1);
    at.setHours(RECEIVING_OPEN_HOUR, 0, 0, 0);
    return { at, rolled: true };
  }
  return { at: d, rolled: false };
}

export function estimateTransit(input: TransitEstimateInput): TransitEstimateResult {
  const {
    miles,
    pickupAt,
    driverType,
    delayMinutes = 0,
    respectReceivingHours = true,
  } = input;

  const safeMiles = Math.max(0, miles || 0);
  const delayHours = Math.max(0, delayMinutes) / 60;

  let cursor = addHours(pickupAt, LOADING_HOURS);
  let drivingHours: number;
  let drivingDays = 1;
  let restBreaks = 0;

  if (driverType === "team") {
    drivingHours = safeMiles / TEAM_DOOR_TO_DOOR_MPH;
    cursor = addHours(cursor, drivingHours + delayHours);
    drivingDays = Math.max(1, Math.ceil(drivingHours / 24));
  } else {
    drivingHours = safeMiles / SOLO_ROAD_MPH;
    // Weather/traffic/construction eats into the same duty clock.
    let remaining = drivingHours + delayHours;
    drivingDays = 0;

    // Safety valve: a day always consumes at least some driving time.
    while (remaining > 0.0001 && drivingDays < 60) {
      const today = Math.min(remaining, SOLO_DRIVE_HOURS_PER_DAY);
      // 30-min break lands inside the duty day once past 8 driving hours.
      const breakHours = today > SOLO_BREAK_AFTER_HOURS ? SOLO_BREAK_HOURS : 0;
      cursor = addHours(cursor, today + breakHours);
      remaining -= today;
      drivingDays += 1;
      if (remaining > 0.0001) {
        cursor = addHours(cursor, SOLO_RESET_HOURS);
        restBreaks += 1;
      }
    }
    drivingDays = Math.max(1, drivingDays);
  }

  let rolledToReceivingHours = false;
  if (respectReceivingHours) {
    const rolled = rollToReceivingWindow(cursor);
    cursor = rolled.at;
    rolledToReceivingHours = rolled.rolled;
  }

  return {
    deliveryAt: cursor,
    totalHours: Math.round(((cursor.getTime() - pickupAt.getTime()) / HOUR_MS) * 10) / 10,
    drivingHours: Math.round(drivingHours * 10) / 10,
    drivingDays,
    restBreaks,
    delayMinutes: Math.round(Math.max(0, delayMinutes)),
    rolledToReceivingHours,
  };
}

/** "Wed, Aug 5 · 2:30 PM" */
export function formatTransitMoment(d: Date): string {
  return d.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** "2 days 4h" / "14h 20m" */
export function formatTransitDuration(totalHours: number): string {
  if (totalHours < 24) {
    const h = Math.floor(totalHours);
    const m = Math.round((totalHours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const days = Math.floor(totalHours / 24);
  const h = Math.round(totalHours - days * 24);
  return h > 0 ? `${days} day${days > 1 ? "s" : ""} ${h}h` : `${days} day${days > 1 ? "s" : ""}`;
}
